"use client";

import { useRef, useState, useEffect, type ReactNode, type RefObject } from "react";
import Link from "next/link";

/* ─── Scroll reveal ───────────────────────────────────────── */
function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, from = "bottom" }: { children: ReactNode; delay?: number; from?: "bottom" | "left" | "right" }) {
  const [ref, v] = useVisible();
  const hidden: Record<"bottom" | "left" | "right", string> = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Shared UI ───────────────────────────────────────────── */
function Eyebrow({ text, light = false }: { text: string; light?: boolean }) {
  return (
    <span className={`block text-[11px] font-bold tracking-[0.15em] uppercase mb-3.5 ${light ? "text-blue-300" : "text-blue-700"}`}>
      {text}
    </span>
  );
}
function Divider({ light = false }) {
  return <div className={`w-11 h-[3px] mb-6 ${light ? "bg-blue-400" : "bg-blue-700"}`} />;
}
/* ─── Data ────────────────────────────────────────────────── */
const THREAT_STATS = [
  { v: "₹14Cr+",  l: "Avg. cost of data breach in India",         src: "IBM 2024" },
  { v: "43%",     l: "Cyberattacks target SMEs & MSMEs",           src: "Verizon DBIR" },
  { v: "60%",     l: "SMEs close within 6 months of major breach", src: "NCSA" },
  { v: "79L+",    l: "Cybersecurity incidents in India (2023)",     src: "CERT-In" },
];

const SERVICES = [
  {
    n: "01", title: "Vulnerability Assessment & Penetration Testing (VAPT)",
    body: "Identify security weaknesses before attackers do. Comprehensive VAPT — network, application, API and infrastructure — with detailed reports, risk ratings and remediation roadmaps.",
    img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&q=80",
    tags: ["Network VAPT", "App & API testing", "Risk-rated reports"],
  },
  {
    n: "02", title: "Security Audit & Risk Assessment",
    body: "Systematic evaluation of your security posture across people, processes and technology. We benchmark against ISO 27001 and NIST, then deliver a prioritised risk remediation plan.",
    img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80",
    tags: ["ISO 27001 / NIST", "People & process audit", "Risk remediation plan"],
  },
  {
    n: "03", title: "SOC-as-a-Service (24/7 Monitoring)",
    body: "A dedicated Security Operations Centre monitoring your infrastructure around the clock. Real-time threat detection, incident response, SIEM management and security alerting — without the cost of an internal team.",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&q=80",
    tags: ["24/7 monitoring", "SIEM management", "Real-time alerts"],
  },
  {
    n: "04", title: "Network Security",
    body: "Firewall configuration, network segmentation, intrusion detection and prevention (IDS/IPS), VPN setup, zero-trust network architecture and network traffic analysis.",
    img: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&q=80",
    tags: ["Zero-trust architecture", "IDS / IPS", "Firewall & VPN"],
  },
  {
    n: "05", title: "Cloud Security",
    body: "Secure cloud configurations, identity and access management (IAM), cloud-native security tools, misconfiguration audits and continuous compliance monitoring for AWS, Azure and GCP.",
    img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&q=80",
    tags: ["AWS / Azure / GCP", "IAM & CSPM", "Misconfiguration audits"],
  },
  {
    n: "06", title: "Compliance & Governance",
    body: "Achieve and maintain compliance with ISO 27001, GDPR, PCI DSS, IT Act 2000, RBI cybersecurity guidelines and SEBI IT framework — with full documentation and audit support.",
    img: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&q=80",
    tags: ["ISO 27001 / GDPR", "RBI & SEBI frameworks", "Audit documentation"],
  },
  {
    n: "07", title: "Incident Response & Forensics",
    body: "Rapid response to active security incidents — containment, eradication, recovery and forensic analysis. We minimise damage and help you understand what happened and how to prevent recurrence.",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&q=80",
    tags: ["Rapid containment", "Digital forensics", "Root cause analysis"],
  },
  {
    n: "08", title: "Security Awareness Training",
    body: "The human element is the #1 attack vector. Structured training programs — phishing simulations, social engineering awareness and security culture workshops for your entire team.",
    img: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80",
    tags: ["Phishing simulations", "Social engineering", "Security culture"],
  },
];

const STEPS = [
  { n: "01", title: "Scope Definition",           body: "Define the boundaries, assets and systems in scope for assessment — aligned with your business risk priorities." },
  { n: "02", title: "Reconnaissance & Discovery", body: "Identify exposed assets, services, open ports and potential attack vectors using passive and active techniques." },
  { n: "03", title: "Vulnerability Assessment",   body: "Systematic scanning and manual testing for security weaknesses across all in-scope systems and applications." },
  { n: "04", title: "Exploitation Testing",        body: "Controlled simulation of real attack techniques to validate actual risk — beyond theoretical vulnerability listing." },
  { n: "05", title: "Reporting",                  body: "Detailed report with findings, risk ratings (Critical/High/Medium/Low), business impact analysis and remediation steps." },
  { n: "06", title: "Remediation Support",        body: "We work alongside your team to implement fixes, provide guidance on complex remediations and verify resolution." },
  { n: "07", title: "Re-testing",                 body: "Validate that all identified vulnerabilities have been successfully resolved — a formal closure report issued." },
  { n: "08", title: "Continuous Monitoring",       body: "Ongoing monitoring to detect and respond to new threats in real time — the cycle continues to protect your business." },
];

const FAQS = [
  { q: "Why is cybersecurity critical for Indian SMEs right now?",     a: "Cyberattacks on Indian businesses increased 300% year-on-year. 43% of attacks target SMEs specifically — because they are seen as easier targets than large enterprises. The average breach costs ₹14 crore+ and 60% of SMEs that suffer a major breach close within 6 months." },
  { q: "What is the difference between VAPT and a security audit?",    a: "A VAPT (Vulnerability Assessment & Penetration Testing) actively tests your systems by simulating real attacks to find exploitable weaknesses. A security audit is a broader review of your security policies, procedures, controls and compliance posture — it evaluates whether your overall security programme is effective." },
  { q: "What is SOC-as-a-Service and do we need it?",                  a: "SOC-as-a-Service provides 24/7 monitoring of your IT environment by a team of security analysts — without you needing to build an internal Security Operations Centre. It&apos;s essential for any business handling sensitive data or running critical systems, where threats need to be detected and responded to in real time." },
  { q: "How long does a VAPT engagement take?",                        a: "A standard network and application VAPT typically takes 1–3 weeks depending on scope. We provide a detailed timeline after the initial scoping call. Re-testing after remediation is included." },
  { q: "Which compliance frameworks do you support?",                   a: "We support ISO 27001, GDPR, PCI DSS, IT Act 2000, RBI Cybersecurity Framework, SEBI IT Framework and SOC 2. We assess your specific regulatory requirements during the first consultation." },
  { q: "What happens if a breach occurs while under your monitoring?", a: "Our SOC team initiates an immediate incident response process — containment, investigation, eradication and recovery. You receive real-time status updates and a detailed post-incident forensics report. Response SLAs are defined in your service agreement." },
];

/* ─── FAQ Item ────────────────────────────────────────────── */
function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      style={{ transitionDelay: `${i * 0.06}s` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex justify-between items-center gap-4 py-[22px] bg-transparent border-none cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-slate-900 leading-snug">{q}</span>
        <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center transition-colors duration-200 ${open ? "bg-blue-700" : "bg-slate-100"}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d={open ? "M2 6h8" : "M6 2v8M2 6h8"} stroke={open ? "#fff" : "#1565C0"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <div className="pb-[22px]">
          <p className="text-[14px] text-slate-500 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Service Card ────────────────────────────────────────── */
function ServiceCard({ s, i }: { s: { tags: string[]; img: string; title: string; body: string; n: string }; i: number }) {
  const [ref, v] = useVisible();
  const [hov, setHov] = useState(false);
  return (
    <div
      ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className={`flex flex-col overflow-hidden bg-white border transition-all duration-500 ${hov ? "border-slate-300 shadow-xl" : "border-slate-200 shadow-sm"}`}
      style={{ transitionDelay: `${(i % 4) * 0.09}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(24px)" }}
    >
      <div className="relative h-[160px] overflow-hidden">
        <img
          src={s.img} alt={s.title}
          className={`w-full h-full object-cover transition-transform duration-500 ${hov ? "scale-105" : "scale-100"}`}
        />
        <div className="absolute top-3.5 left-3.5 bg-blue-700 text-white text-[11px] font-bold tracking-[0.08em] px-2.5 py-1">
          {s.n}
        </div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-[13.5px] font-bold text-slate-900 mb-2 leading-snug">{s.title}</p>
        <p className="text-[13px] text-slate-500 leading-relaxed mb-3.5">{s.body}</p>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {s.tags.map(tag => (
            <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 tracking-wide">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Step ────────────────────────────────────────────────── */
function Step({ s, i, last }: { s: { n: string; title: string; body: string }; i: number; last?: boolean }) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`flex gap-4 items-start relative transition-all duration-500 ${!last ? "pb-7" : ""}`}
      style={{ transitionDelay: `${(i % 4) * 0.08}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(20px)" }}
    >
      {!last && <div className="absolute left-[17px] top-9 w-px bottom-0 bg-slate-200" />}
      <div className="w-9 h-9 bg-blue-700 flex-shrink-0 flex items-center justify-center">
        <span className="text-[11px] font-bold text-white">{s.n}</span>
      </div>
      <div className="pt-1.5">
        <p className="text-[14px] font-bold text-slate-900 mb-1.5">{s.title}</p>
        <p className="text-[13.5px] text-slate-500 leading-relaxed">{s.body}</p>
      </div>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function CybersecurityPage() {
  return (
    <main className="bg-white">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center px-4 sm:px-8 lg:px-20 py-[30px] bg-white">
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
            <Link href="/services" className="text-blue-700 font-medium hover:underline">Services</Link>
            <span className="text-slate-300">›</span>
            <Link href="/services/it" className="text-blue-700 font-medium hover:underline">IT Solutions</Link>
            <span className="text-slate-300">›</span>
            <span>Cybersecurity</span>
          </nav>

          <div className="flex flex-wrap gap-2 mb-5">
            {["VAPT", "SOC-as-a-Service", "ISO 27001", "Cloud Security", "Incident Response"].map(tag => (
              <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 tracking-wide">{tag}</span>
            ))}
          </div>

          <Eyebrow text="IT Solutions — Cybersecurity" />
          <Divider />

          <h1 className="text-[clamp(26px,3.5vw,46px)] font-extrabold text-slate-900 leading-[1.1] mb-5 ">
            Protect What You&apos;ve Built — End-to-End Cybersecurity
          </h1>
          <p className="text-[16px] font-light text-slate-500 leading-relaxed mb-9 ">
            With cyberattacks on Indian businesses increasing 300% year-on-year, professional cybersecurity is no longer optional. Farsight provides enterprise-grade security to protect your data, infrastructure, reputation and business continuity.
          </p>
          <div className="flex gap-3.5">
            <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
              BOOK SECURITY CONSULTATION
            </button>
            <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
              FREE RISK ASSESSMENT
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden">
          <img
            src="/images/services/IT/Banner/cybersecurity.png"
            alt="Cybersecurity protection"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Blue stat strip */}
      <div className="bg-blue-700 grid grid-cols-2 lg:grid-cols-4">
        {[
          { v: "300%",  l: "Rise in Attacks (YoY)" },
          { v: "8",     l: "Security Services" },
          { v: "24/7",  l: "SOC Monitoring" },
          { v: "Free",  l: "Risk Assessment" },
        ].map((s, i) => (
          <div key={i} className={`py-5 text-center ${i < 3 ? "border-r border-white/15" : ""}`}>
            <p className="text-[22px] font-black text-white leading-none">{s.v}</p>
            <p className="text-[11px] font-medium text-white/65 mt-1.5 tracking-widest uppercase">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ══ THREAT LANDSCAPE ═══════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-900">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <Eyebrow text="The Threat Landscape" light />
            <Divider light />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-white leading-tight mb-5">
              The Risk Is Real — and Growing
            </h2>
            <p className="text-[15px] font-light text-slate-300 leading-relaxed mb-8">
              Indian SMEs and startups are increasingly targeted by ransomware, phishing and data breaches — often lacking the in-house expertise to defend against sophisticated attacks. The cost goes far beyond financial loss.
            </p>
            <div className="flex flex-col gap-4">
              {[
                "Average cost of a data breach in India: ₹14 crore+ (IBM 2024)",
                "43% of cyberattacks specifically target small and medium businesses",
                "60% of SMEs that suffer a major breach close within 6 months",
                "India reported 79 lakh+ cybersecurity incidents in 2023 (CERT-In)",
              ].map((stat, i) => (
                <div key={i} className="flex items-start gap-3.5">
                  <div className="w-5 h-5 bg-blue-600 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-[14px] text-slate-300 leading-relaxed">{stat}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="grid grid-cols-2 gap-0.5">
              {THREAT_STATS.map((s, i) => (
                <div key={i} className="bg-slate-800 border border-slate-700 p-6">
                  <p className="text-[32px] font-black text-blue-400 leading-none mb-2">{s.v}</p>
                  <p className="text-[13px] text-slate-300 leading-snug mb-2">{s.l}</p>
                  <p className="text-[11px] font-semibold text-slate-500 tracking-wide uppercase">{s.src}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ SERVICES — 4-col grid ══════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <Eyebrow text="Cybersecurity Services" />
              <Divider />
              <div className="flex justify-between items-end flex-wrap gap-4">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight m-0">
                  Eight Security Capabilities.<br />Complete Protection.
                </h2>
                <p className="text-[14px] text-slate-500 max-w-[360px] leading-relaxed">
                  From proactive testing to 24/7 monitoring and rapid incident response — full-spectrum security coverage.
                </p>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s, i) => <ServiceCard key={i} s={s} i={i} />)}
          </div>
        </div>
      </section>

      {/* ══ 8-STEP PROCESS ═════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Our Security Process" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              8-Step Security Assessment Process
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-10">
              A rigorous, structured methodology used across all our security engagements — from initial scoping to continuous monitoring. No shortcuts, no checklists-only assessments.
            </p>
            <img
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=700&q=80"
              alt="Cybersecurity process"
              className="w-full h-[260px] object-cover"
            />
          </Reveal>

          {/* 2-col steps: 1-4 left, 5-8 right */}
          <Reveal from="right" delay={0.1}>
            <div className="grid grid-cols-2 gap-x-10 gap-y-0">
              <div>
                {STEPS.slice(0, 4).map((s, i) => (
                  <Step key={i} s={s} i={i} last={i === 3} />
                ))}
              </div>
              <div>
                {STEPS.slice(4).map((s, i) => (
                  <Step key={i} s={s} i={i} last={i === 3} />
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FAQ ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Frequently Asked Questions" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-slate-900 leading-tight mb-6">
              Common Questions About Cybersecurity
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Clear, honest answers to the cybersecurity questions we hear most from Indian businesses.
            </p>
            <img
              src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=700&q=80"
              alt="Cybersecurity FAQ"
              className="w-full h-[180px] sm:h-[240px] lg:h-[280px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-slate-200">
              {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <video
              src="/videos/CTA.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-[220px] sm:h-[300px] lg:h-[380px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <Eyebrow text="Is Your Business Secure?" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Book a Free Security Consultation
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-3">
              We assess your current risk posture and identify your most critical vulnerabilities — at no cost, no commitment required.
            </p>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Most businesses discover at least 3–5 critical vulnerabilities in their first assessment. The question is whether you find them before attackers do.
            </p>
            <div className="flex gap-3.5 flex-wrap">
              <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-8 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
                BOOK SECURITY CONSULTATION
              </button>
              <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-8 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
                FREE RISK ASSESSMENT
              </button>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}